import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Eye, ClipboardCheck, UserRound, CalendarCheck, Instagram } from "lucide-react";
import { COMPANY, COPY, DEALER } from "@/lib/company";
import { usePageMeta } from "@/lib/seo";
import LocationMap from "@/components/LocationMap";
import ReviewSlider from "@/components/ReviewSlider";

const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const VALUES = [
  { icon: Eye, title: "Auswahl", text: "Wir konzentrieren uns auf Sportwagen und Premiumfahrzeuge, die uns selbst überzeugen." },
  { icon: ClipboardCheck, title: "Transparenz", text: "Klare Fahrzeugdaten und ehrliche Kommunikation – vom ersten Kontakt bis zur Übergabe." },
  { icon: UserRound, title: "Persönlich", text: "Ein fester Ansprechpartner, kurze Wege und schnelle Antworten." },
  { icon: CalendarCheck, title: "Termin", text: "Besichtigung und Probefahrt nehmen wir uns Zeit – nach Ihrer Vereinbarung." },
];

export default function About() {
  usePageMeta("Über uns", `${COMPANY.legalName} – ${COPY.tagline} ${COPY.heroLine}`);
  return (
    <div data-testid="about-page" className="pt-28 md:pt-36 pb-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <p className="eyebrow">Über uns</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1] text-ink mt-5 max-w-4xl tracking-tight">
          {COPY.heroTitle}<br /><span className="chrome-text">{COPY.heroSubtitle}</span>
        </h1>

        <div className="grid lg:grid-cols-12 gap-12 mt-16 mb-24">
          <motion.div {...reveal} className="lg:col-span-7 space-y-5 text-dim leading-relaxed text-base md:text-lg">
            <p>
              {COMPANY.legalName} ist {COMPANY.since ? `seit ${COMPANY.since} ` : ""}Ihr Ansprechpartner für Sportwagen und Premiumfahrzeuge in {COMPANY.region}.
              Unser Bestand ist bewusst klein gehalten – jedes Fahrzeug wird von uns persönlich ausgewählt.
            </p>
            <p>
              Sie erreichen uns direkt per WhatsApp, Telefon oder E-Mail. Besichtigungen und Probefahrten
              vereinbaren wir individuell an unserem Standort in der {COMPANY.street}, {COMPANY.city}.
            </p>
            {COMPANY.instagram && (
              <p>Neue Fahrzeuge, Übergaben und Einblicke in unseren Showroom zeigen wir auf Instagram – folgen Sie uns unter {COMPANY.instagram.handle}.</p>
            )}
            <div className="flex flex-wrap gap-3 !mt-10">
              <Link to="/fahrzeuge" data-testid="about-vehicles-cta"
                className="btn-pill inline-flex items-center gap-2 bg-action text-action-ink hover:bg-action-hover font-semibold px-8 py-4 text-sm">
                Zum Fahrzeugbestand <ArrowRight size={16} />
              </Link>
              {COMPANY.instagram && (
                <a href={COMPANY.instagram.url} target="_blank" rel="noopener noreferrer" data-testid="about-instagram-btn"
                  className="btn-pill inline-flex items-center gap-2 border border-line bg-surface text-ink hover:border-ink/40 font-semibold px-7 py-4 text-sm transition-colors">
                  <Instagram size={16} strokeWidth={1.8} className="text-[#E1306C]" /> {COMPANY.instagram.handle}
                </a>
              )}
            </div>
          </motion.div>
          <motion.div {...reveal} className="lg:col-span-5 grid grid-cols-2 gap-px bg-line rounded-xl overflow-hidden border border-line self-start">
            {VALUES.map(({ icon: Icon, title, text }) => (
              <div key={title} className="bg-surface p-6 md:p-7">
                <Icon size={22} strokeWidth={1.5} className="text-brand mb-4" />
                <h3 className="font-display font-bold text-ink mb-2">{title}</h3>
                <p className="text-xs text-dim leading-relaxed">{text}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div {...reveal}><LocationMap testId="about-map" /></motion.div>
        {DEALER.googleReviews && <motion.div {...reveal} className="mt-12"><ReviewSlider data={DEALER.googleReviews} /></motion.div>}
      </div>
    </div>
  );
}
